import * as React from 'react'
import './styles/index.css'

import type { Content, JSONContent } from '@tiptap/react'
import type { UseMinimalTiptapEditorProps } from './hooks/use-minimal-tiptap'
import { EditorContent } from '@tiptap/react'
import { cn } from '@/lib/utils'
import { LinkBubbleMenu } from './components/bubble-menu/link-bubble-menu'
import { useMinimalTiptapEditor } from './hooks/use-minimal-tiptap'
import { MeasuredContainer } from './components/measured-container'
import { Toolbar } from './components/toolbar'
import { DefaultFormatting } from './types'
import { SelectionBubbleMenu } from './components/bubble-menu/selection-bubble-menu'

export interface MinimalTiptapProps extends Omit<UseMinimalTiptapEditorProps, 'onUpdate'> {
  value?: Content
  onChange?: (value: Content) => void
  className?: string
  editorContentClassName?: string
  hideToolbar?: boolean

  mode?: "edit" | "read"
  defaultFormatting?: DefaultFormatting
}

export const MinimalTiptapEditor = React.forwardRef<HTMLDivElement, MinimalTiptapProps>(
  ({ value, onChange, className, editorContentClassName, hideToolbar=false, mode="edit", editable, ...props }, ref) => {

    const editor = useMinimalTiptapEditor({
      value: value,
      onUpdate: (content) => {
        onChange?.(content)
      },
      placeholder: "Start met typen",
      editable: editable,
      ...props
    })

    React.useEffect(() => {
      if (props.defaultFormatting && editor) {
        // First clear any existing content if the editor is empty
        if (editor.isEmpty) {
          // Focus the editor to ensure we can set formatting
          editor.commands.focus();
          
          // Apply default marks
          props.defaultFormatting.marks?.forEach(mark => {
            editor.commands.setMark(mark.type, mark.attrs);
          });
          
          // Apply default node if specified
          if (props.defaultFormatting.node) {
            editor.commands.setNode(props.defaultFormatting.node.type, props.defaultFormatting.node.attrs);
          }
        }
      }
    }, [editor]);

    if (!editor) {
      return null
    }

    if(mode === 'read') {
      return (
        <MeasuredContainer
          as="div"
          name="editor"
          ref={ref}
          className={cn(
            'flex h-auto w-full flex-col',
            className
          )}
        >
          <EditorContent editor={editor} contentEditable={false} className={cn('minimal-tiptap-editor', editorContentClassName)} />
        </MeasuredContainer>
      )
    }

    return (
      <MeasuredContainer
        as="div"
        name="editor"
        ref={ref}
        className={cn(
          'flex h-auto w-full flex-col rounded-md border border-input shadow-sm',
          className
        )}
      >
        {(!hideToolbar && editable) && <Toolbar editor={editor} />}
        <EditorContent editor={editor} className={cn('p-2 minimal-tiptap-editor', editorContentClassName)} />
        <LinkBubbleMenu editor={editor} />
        {(hideToolbar && editable) && <SelectionBubbleMenu editor={editor}><></></SelectionBubbleMenu>}
      </MeasuredContainer>
    )
  }
)

MinimalTiptapEditor.displayName = 'MinimalTiptapEditor'

export default MinimalTiptapEditor